import {
  ChangeDetectionStrategy, Component, EventEmitter, Output, signal, inject, OnInit,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { AdminService, AdminForbiddenError } from '../../services/admin.service.js';
import { FirebaseService } from '../../services/firebase.service.js';
import type {
  AdminUser, AdminUserDetail, UserActivity, QuotaKind, QuotaOverrideRequest,
} from '../../models/admin.types.js';

type OverrideEditValue = number | null | 'unset';
type OverrideEditMap = Partial<Record<QuotaKind, OverrideEditValue>>;

@Component({
  selector: 'app-admin-users',
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="p-6 space-y-4">
      <!-- Search bar -->
      <div class="flex items-center gap-2">
        <input type="text"
               [value]="query()"
               (input)="query.set($any($event.target).value)"
               (keydown.enter)="search(0)"
               placeholder="Email, имя, uid или twitch_sub…"
               class="flex-1 bg-gray-950 border border-gray-800 rounded px-3 py-2 text-sm text-gray-100 focus:border-purple-500 focus:ring-1 focus:ring-purple-500 outline-none" />
        <button (click)="search(0)" [disabled]="loading()"
                class="px-4 py-2 bg-purple-600/30 hover:bg-purple-600/50 disabled:opacity-50 text-purple-200 border border-purple-700/50 rounded text-sm font-semibold transition-colors">
          {{ loading() ? '…' : 'Найти' }}
        </button>
      </div>

      @if (error()) {
        <div class="p-3 bg-red-950/40 border border-red-900/60 rounded text-sm text-red-300">{{ error() }}</div>
      }

      <!-- Users table -->
      <div class="bg-gray-950 border border-gray-800 rounded-lg overflow-hidden">
        <table class="w-full text-xs">
          <thead class="bg-gray-900 text-gray-200 uppercase tracking-wider font-semibold">
            <tr>
              <th class="text-left px-3 py-2 font-medium">Имя</th>
              <th class="text-left px-3 py-2 font-medium">Email</th>
              <th class="text-left px-3 py-2 font-medium">Pro</th>
              <th class="text-left px-3 py-2 font-medium">Роль</th>
              <th class="text-left px-3 py-2 font-medium">Создан</th>
              <th class="text-left px-3 py-2 font-medium">Последний вход</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-900">
            @for (u of users(); track u.uid) {
              <tr (click)="openUser(u.uid)"
                  class="cursor-pointer hover:bg-gray-900/60 transition-colors"
                  [class.opacity-50]="u.deleted_at">
                <td class="px-3 py-2 text-gray-200">{{ u.display_name || '—' }}</td>
                <td class="px-3 py-2 text-gray-400 font-mono">{{ u.email || '—' }}</td>
                <td class="px-3 py-2">
                  @if (u.is_pro) { <span class="px-1.5 py-0.5 bg-purple-900/40 text-purple-300 rounded">PRO</span> }
                  @else          { <span class="text-gray-600">—</span> }
                </td>
                <td class="px-3 py-2">
                  @if (u.role === 'admin') { <span class="px-1.5 py-0.5 bg-amber-900/40 text-amber-300 rounded">ADMIN</span> }
                  @else                    { <span class="text-gray-600">user</span> }
                </td>
                <td class="px-3 py-2 text-gray-500">{{ formatDate(u.created_at) }}</td>
                <td class="px-3 py-2 text-gray-500">{{ u.last_login_at ? formatDate(u.last_login_at) : '—' }}</td>
              </tr>
            } @empty {
              <tr><td colspan="6" class="px-3 py-8 text-center text-gray-500 italic">Ничего не найдено</td></tr>
            }
          </tbody>
        </table>
      </div>

      @if (total() > limit()) {
        <div class="flex items-center justify-between text-xs text-gray-500">
          <span>Показано {{ users().length }} из {{ total() }}</span>
          <div class="flex gap-2">
            <button (click)="search(offset() - limit())" [disabled]="offset() === 0 || loading()"
                    class="px-3 py-1 bg-gray-800 hover:bg-gray-700 disabled:opacity-50 rounded">←</button>
            <button (click)="search(offset() + limit())" [disabled]="offset() + limit() >= total() || loading()"
                    class="px-3 py-1 bg-gray-800 hover:bg-gray-700 disabled:opacity-50 rounded">→</button>
          </div>
        </div>
      }
    </div>

    <!-- User detail drawer -->
    @if (selectedUser(); as detail) {
      <div class="fixed inset-0 z-[110] bg-black/60 flex justify-end" (click)="closeDrawer()">
        <div class="bg-gray-900 border-l border-gray-800 w-full max-w-2xl h-full overflow-y-auto p-6 space-y-5"
             (click)="$event.stopPropagation()">
          <header class="flex items-start justify-between gap-4">
            <div class="flex items-center gap-3 min-w-0">
              @if (detail.pg.photo_url) {
                <img [src]="detail.pg.photo_url" class="w-12 h-12 rounded-full border border-gray-700 shrink-0">
              }
              <div class="min-w-0">
                <p class="text-base font-bold text-gray-100 truncate">{{ detail.pg.display_name || '—' }}</p>
                <p class="text-xs text-gray-400 font-mono truncate">{{ detail.pg.email || 'email отсутствует' }}</p>
                <p class="text-xs text-gray-500 font-mono truncate mt-0.5">uid: {{ detail.pg.uid }}</p>
              </div>
            </div>
            <button (click)="closeDrawer()" class="p-1 text-gray-500 hover:text-white">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/></svg>
            </button>
          </header>

          <!-- Status badges -->
          <div class="flex flex-wrap gap-2 text-xs font-semibold">
            @if (detail.pg.is_pro) { <span class="px-2 py-0.5 bg-purple-900/40 text-purple-300 rounded">PRO</span> }
            @else                  { <span class="px-2 py-0.5 bg-gray-800 text-gray-400 rounded">FREE</span> }
            @if (detail.pg.role === 'admin') { <span class="px-2 py-0.5 bg-amber-900/40 text-amber-300 rounded">ADMIN</span> }
            @if (detail.pg.deleted_at) { <span class="px-2 py-0.5 bg-red-900/40 text-red-300 rounded">УДАЛЁН</span> }
            @if (detail.pg.consent_accepted_at) { <span class="px-2 py-0.5 bg-emerald-900/40 text-emerald-300 rounded">consent v{{ detail.pg.consent_policy_version }}</span> }
          </div>

          <!-- PG facts -->
          <section>
            <p class="text-xs text-gray-300 uppercase tracking-wider font-semibold mb-2">PG (РФ)</p>
            <dl class="grid grid-cols-2 gap-2 text-xs">
              <dt class="text-gray-500">twitch_sub</dt><dd class="text-gray-300 font-mono">{{ detail.pg.twitch_sub }}</dd>
              <dt class="text-gray-500">создан</dt><dd class="text-gray-300">{{ formatDate(detail.pg.created_at) }}</dd>
              <dt class="text-gray-500">последний вход</dt><dd class="text-gray-300">{{ detail.pg.last_login_at ? formatDate(detail.pg.last_login_at) : '—' }}</dd>
              <dt class="text-gray-500">активных сессий</dt><dd class="text-gray-300">{{ detail.activeSessions }}</dd>
              <dt class="text-gray-500">152-ФЗ согласие</dt>
              <dd class="text-gray-300">
                @if (detail.pg.consent_cross_border && detail.pg.consent_age18) { ✓ }
                @else { <span class="text-amber-400">не подтверждено</span> }
              </dd>
            </dl>
          </section>

          <!-- Firestore counters -->
          <section>
            <p class="text-xs text-gray-300 uppercase tracking-wider font-semibold mb-2">Firestore (counters)</p>
            <dl class="grid grid-cols-2 gap-2 text-xs">
              <dt class="text-gray-500">клипы</dt><dd class="text-gray-300">{{ detail.firestore.clipCount }}</dd>
              <dt class="text-gray-500">ZIP</dt><dd class="text-gray-300">{{ detail.firestore.zipCount }}</dd>
              <dt class="text-gray-500">AI Quick</dt><dd class="text-gray-300">{{ detail.firestore.aiQuickCount }}</dd>
              <dt class="text-gray-500">AI Deep</dt><dd class="text-gray-300">{{ detail.firestore.aiDeepCount }}</dd>
              <dt class="text-gray-500">AI Search</dt><dd class="text-gray-300">{{ detail.firestore.aiSearchCount }}</dd>
              <dt class="text-gray-500">cloud-sync</dt><dd class="text-gray-300">{{ detail.firestore.syncEnabled ? 'включён' : 'выключен' }}</dd>
              <dt class="text-gray-500">StreamMarkers</dt><dd class="text-gray-300">{{ detail.markerSources }} копий</dd>
            </dl>
          </section>

          <!-- Daily quota -->
          <section>
            <div class="flex items-center justify-between mb-2">
              <p class="text-xs text-gray-300 uppercase tracking-wider font-semibold">Дневные квоты</p>
              <p class="text-[11px] text-gray-500">Сброс {{ formatResetClock(detail.firestore.dailyQuota.resetAt) }}</p>
            </div>
            <table class="w-full text-xs border border-gray-800 rounded overflow-hidden">
              <thead class="bg-gray-900/60 text-gray-400 uppercase tracking-wider text-[10px]">
                <tr>
                  <th class="text-left px-2 py-1 font-medium">Тип</th>
                  <th class="text-right px-2 py-1 font-medium">Сегодня</th>
                  <th class="text-right px-2 py-1 font-medium">Лимит</th>
                  <th class="text-left px-2 py-1 font-medium">Override</th>
                </tr>
              </thead>
              <tbody class="divide-y divide-gray-900">
                @for (k of QUOTA_KINDS; track k) {
                  <tr>
                    <td class="px-2 py-1 text-gray-400">{{ kindLabel(k) }}</td>
                    <td class="px-2 py-1 text-gray-200 text-right font-mono">{{ detail.firestore.dailyQuota[k].used }}</td>
                    <td class="px-2 py-1 text-gray-200 text-right font-mono">
                      {{ detail.firestore.dailyQuota[k].limit ?? '∞' }}
                      @if (detail.firestore.dailyQuota[k].hasOverride) {
                        <span class="text-amber-400" title="установлен per-user override">*</span>
                      }
                    </td>
                    <td class="px-2 py-1">
                      <input type="text" inputmode="numeric"
                             [value]="renderOverrideInput(k)"
                             (input)="onOverrideEdit(k, $any($event.target).value)"
                             placeholder="(default)"
                             [class.ring-1]="detail.firestore.dailyQuota[k].hasOverride"
                             [class.ring-amber-700]="detail.firestore.dailyQuota[k].hasOverride"
                             class="w-20 bg-gray-950 border border-gray-800 rounded px-1.5 py-0.5 text-xs font-mono text-gray-200 focus:border-purple-500 focus:ring-1 focus:ring-purple-500 outline-none"/>
                    </td>
                  </tr>
                }
              </tbody>
            </table>
            @if (detail.firestore.dailyQuota.overrideReason) {
              <p class="text-[11px] text-amber-400 mt-1">Текущая причина: {{ detail.firestore.dailyQuota.overrideReason }}</p>
            }
            <div class="flex gap-2 mt-2">
              <input type="text"
                     [value]="overrideReason()"
                     (input)="overrideReason.set($any($event.target).value)"
                     placeholder="Причина override (обязательно)"
                     class="flex-1 bg-gray-950 border border-gray-800 rounded px-2 py-1 text-xs text-gray-200 focus:border-purple-500 focus:ring-1 focus:ring-purple-500 outline-none"/>
              <button (click)="saveOverride(detail)" [disabled]="busy() || !hasOverrideEdits()"
                      class="px-3 py-1 bg-purple-600/20 hover:bg-purple-600/40 disabled:opacity-50 disabled:cursor-not-allowed text-purple-200 border border-purple-700/50 rounded text-xs font-semibold transition-colors">
                Сохранить override
              </button>
              <button (click)="resetQuota(detail)" [disabled]="busy()"
                      class="px-3 py-1 bg-amber-700/20 hover:bg-amber-700/40 disabled:opacity-50 text-amber-200 border border-amber-700/50 rounded text-xs font-semibold transition-colors">
                Сбросить квоту
              </button>
            </div>
            <p class="text-[10px] text-gray-500 mt-1">
              Override-поле <span class="text-amber-400">подсвечено амбером</span>, если override стоит. Очистить = снять override (вернуть к тарифу).
              <code>null</code> = безлимит. Целое ≥ 0 = override-лимит. <code>0</code> = блок.
            </p>
          </section>

          <!-- Actions -->
          <section class="space-y-2">
            <p class="text-xs text-gray-300 uppercase tracking-wider font-semibold">Действия</p>

            <div class="flex gap-2">
              <button (click)="togglePro(detail)" [disabled]="busy()"
                      class="flex-1 px-3 py-2 bg-purple-600/20 hover:bg-purple-600/40 disabled:opacity-50 text-purple-200 border border-purple-700/50 rounded text-xs font-semibold transition-colors">
                {{ detail.pg.is_pro ? 'Снять PRO' : 'Выдать PRO' }}
              </button>
              <button (click)="refreshClaims(detail)" [disabled]="busy()"
                      class="flex-1 px-3 py-2 bg-gray-800 hover:bg-gray-700 disabled:opacity-50 text-gray-200 border border-gray-700 rounded text-xs font-semibold transition-colors">
                Обновить claims
              </button>
            </div>

            <div class="flex gap-2">
              @if (detail.pg.role === 'user') {
                <button (click)="setRole(detail, 'admin')" [disabled]="busy()"
                        class="flex-1 px-3 py-2 bg-amber-700/20 hover:bg-amber-700/40 disabled:opacity-50 text-amber-200 border border-amber-700/50 rounded text-xs font-semibold transition-colors">
                  Сделать админом
                </button>
              } @else {
                <button (click)="setRole(detail, 'user')" [disabled]="busy() || isSelf(detail)"
                        [title]="isSelf(detail) ? 'Нельзя демонтировать самого себя' : ''"
                        class="flex-1 px-3 py-2 bg-amber-700/20 hover:bg-amber-700/40 disabled:opacity-50 disabled:cursor-not-allowed text-amber-200 border border-amber-700/50 rounded text-xs font-semibold transition-colors">
                  Снять админа
                </button>
              }
            </div>

            <!-- Danger zone -->
            <div class="mt-3 p-3 border border-red-900/50 bg-red-950/20 rounded-lg space-y-2">
              <p class="text-xs text-red-300 uppercase tracking-wider font-bold">Опасная зона</p>
              <button (click)="softDelete(detail)" [disabled]="busy() || isSelf(detail) || !!detail.pg.deleted_at"
                      [title]="isSelf(detail) ? 'Нельзя удалить себя' : (detail.pg.deleted_at ? 'Уже удалён' : '')"
                      class="w-full px-3 py-2 bg-red-700/30 hover:bg-red-700/50 disabled:opacity-50 disabled:cursor-not-allowed text-red-200 border border-red-700/50 rounded text-xs font-semibold transition-colors">
                Soft-delete (DSAR)
              </button>
            </div>

            @if (actionError()) {
              <p class="text-xs text-red-400">{{ actionError() }}</p>
            }
            @if (actionMessage()) {
              <p class="text-xs text-emerald-400">{{ actionMessage() }}</p>
            }
          </section>

          <!-- Activity -->
          <section>
            <div class="flex items-center justify-between mb-2">
              <p class="text-xs text-gray-300 uppercase tracking-wider font-semibold">Активность</p>
              <button (click)="loadActivity(detail.pg.uid)" [disabled]="loadingActivity()"
                      class="text-xs text-gray-300 hover:text-purple-300 font-semibold">
                {{ loadingActivity() ? 'загрузка…' : (activity() ? 'обновить' : 'показать') }}
              </button>
            </div>
            @if (activity(); as a) {
              <div class="grid grid-cols-2 gap-3 text-xs">
                <div class="bg-gray-950 border border-gray-800 rounded p-2">
                  <p class="text-gray-500 mb-1">AI calls (последние {{ a.aiCalls.length }})</p>
                  <ul class="space-y-1">
                    @for (e of a.aiCalls; track e.id) {
                      <li class="font-mono text-gray-400 truncate">{{ e.type || '—' }} · {{ e.status || '—' }} · {{ e.totalTokens || 0 }}t</li>
                    } @empty {
                      <li class="text-gray-600 italic">пусто</li>
                    }
                  </ul>
                </div>
                <div class="bg-gray-950 border border-gray-800 rounded p-2">
                  <p class="text-gray-500 mb-1">Загрузки (последние {{ a.downloads.length }})</p>
                  <ul class="space-y-1">
                    @for (e of a.downloads; track e.id) {
                      <li class="font-mono text-gray-400 truncate">{{ e.type || '—' }} · {{ e.status || '—' }} · {{ formatBytes(e.bytes) }}</li>
                    } @empty {
                      <li class="text-gray-600 italic">пусто</li>
                    }
                  </ul>
                </div>
              </div>
            }
          </section>
        </div>
      </div>
    }
  `,
})
export class AdminUsersComponent implements OnInit {
  @Output() forbidden = new EventEmitter<void>();
  private adminService = inject(AdminService);
  private firebase = inject(FirebaseService);

  query = signal('');
  users = signal<AdminUser[]>([]);
  total = signal(0);
  offset = signal(0);
  limit = signal(50);
  loading = signal(false);
  error = signal<string | null>(null);

  selectedUser = signal<AdminUserDetail | null>(null);
  activity = signal<UserActivity | null>(null);
  loadingActivity = signal(false);

  busy = signal(false);
  actionError = signal<string | null>(null);
  actionMessage = signal<string | null>(null);

  // Override editing model:
  //   - On user open, `overrideEdits` is pre-filled with the current per-user
  //     overrides from the server (so the row's input shows e.g. "10" instead
  //     of an empty placeholder when an override is active).
  //   - Operator types a new value → that staged change ships in the diff.
  //   - Operator clears a previously-filled input → if the user actually had
  //     an override on the server, the diff sends `'unset'` (server deletes
  //     the field, falling back to the tier default).
  //   - Operator types literal `null` → explicit unlimited override.
  //   - On Save, only fields that DIFFER from the server-side initial state
  //     are sent (so saving a row that wasn't touched is a no-op).
  readonly QUOTA_KINDS: QuotaKind[] = ['aiQuick', 'aiDeep', 'aiSearch', 'clip', 'zip'];
  overrideEdits = signal<OverrideEditMap>({});
  overrideReason = signal<string>('');

  ngOnInit() {
    this.search(0);
  }

  async search(offset: number) {
    this.loading.set(true);
    this.error.set(null);
    try {
      const r = await this.adminService.searchUsers(this.query(), this.limit(), offset);
      this.users.set(r.users);
      this.total.set(r.total);
      this.offset.set(r.offset);
    } catch (err: any) {
      if (err instanceof AdminForbiddenError) { this.forbidden.emit(); return; }
      this.error.set(err?.message || 'Ошибка поиска');
    } finally {
      this.loading.set(false);
    }
  }

  async openUser(uid: string) {
    this.actionError.set(null);
    this.actionMessage.set(null);
    this.activity.set(null);
    try {
      const detail = await this.adminService.getUser(uid);
      this.applyDetail(detail);
    } catch (err: any) {
      if (err instanceof AdminForbiddenError) { this.forbidden.emit(); return; }
      this.error.set(err?.message || 'Ошибка');
    }
  }

  /**
   * Set selected detail + seed override-edit state from current server values.
   * Called on initial open and after each saveOverride/resetQuota refresh so
   * the form reflects what's actually stored. Without this, operator clears
   * an input thinking «remove override» but it's a no-op (delete from diff).
   */
  private applyDetail(detail: AdminUserDetail) {
    this.selectedUser.set(detail);
    const seed: OverrideEditMap = {};
    for (const k of this.QUOTA_KINDS) {
      const cell = detail.firestore.dailyQuota[k];
      if (cell.hasOverride) {
        seed[k] = cell.override;
      }
    }
    this.overrideEdits.set(seed);
    this.overrideReason.set('');
  }

  closeDrawer() {
    this.selectedUser.set(null);
    this.activity.set(null);
    this.actionError.set(null);
    this.actionMessage.set(null);
    this.overrideEdits.set({});
    this.overrideReason.set('');
  }

  // ── Quota override editing ────────────────────────────────────────────

  /**
   * Render a row's input value back to a string. The form is pre-seeded with
   * current server overrides on user open, so an existing override shows up
   * (e.g. "10") instead of the placeholder. `null` reads back as the literal
   * word for clarity; `'unset'` is a transient state visible only between
   * clearing and saving — render as empty so the operator's "cleared" intent
   * doesn't keep displaying as something.
   */
  renderOverrideInput(kind: QuotaKind): string {
    const v = this.overrideEdits()[kind];
    if (v === undefined) return '';
    if (v === null) return 'null';
    if (v === 'unset') return '';
    return String(v);
  }

  /**
   * Translate textbox input into the staged override value. Distinguishes:
   *   - empty string + had server-side override → `'unset'` (signals removal)
   *   - empty string + no server override        → undefined (no diff to send)
   *   - literal `null`                           → null (explicit unlimited)
   *   - integer ≥ 0                              → that number (hard cap)
   *   - everything else                          → invalid, drop staged value
   */
  onOverrideEdit(kind: QuotaKind, raw: string) {
    const trimmed = (raw || '').trim();
    const detail = this.selectedUser();
    const initialHasOverride = !!detail?.firestore.dailyQuota[kind].hasOverride;
    const next: OverrideEditMap = { ...this.overrideEdits() };
    if (trimmed === '') {
      if (initialHasOverride) next[kind] = 'unset';
      else delete next[kind];
    } else if (trimmed.toLowerCase() === 'null') {
      next[kind] = null;
    } else {
      const n = Number(trimmed);
      if (Number.isInteger(n) && n >= 0) next[kind] = n;
      else delete next[kind]; // invalid — silently drop stage
    }
    this.overrideEdits.set(next);
  }

  /** True iff at least one row diverges from the server's current state. */
  hasOverrideEdits(): boolean {
    const edits = this.overrideEdits();
    const detail = this.selectedUser();
    if (!detail) return false;
    for (const k of this.QUOTA_KINDS) {
      const editVal = edits[k];
      if (editVal === undefined) continue;
      const cell = detail.firestore.dailyQuota[k];
      if (editVal === 'unset') {
        if (cell.hasOverride) return true; // genuine removal of an override
        continue;
      }
      // editVal is number | null
      const initialVal: number | null | undefined = cell.hasOverride ? cell.override : undefined;
      if (editVal !== initialVal) return true;
    }
    return false;
  }

  kindLabel(k: QuotaKind): string {
    return ({
      aiQuick: 'AI Quick',
      aiDeep: 'AI Deep',
      aiSearch: 'AI Search',
      clip: 'Клип',
      zip: 'ZIP',
    } as Record<QuotaKind, string>)[k];
  }

  formatResetClock(iso: string | null | undefined): string {
    if (!iso) return '— не установлен';
    try {
      const d = new Date(iso);
      const hh = String(d.getUTCHours()).padStart(2, '0');
      const mm = String(d.getUTCMinutes()).padStart(2, '0');
      return `${d.toUTCString().slice(0, 16)} ${hh}:${mm} UTC`;
    } catch { return iso; }
  }

  async saveOverride(detail: AdminUserDetail) {
    if (!this.hasOverrideEdits()) return;
    const reason = this.overrideReason().trim();
    if (!reason) {
      this.actionError.set('Укажите причину override (для аудита).');
      return;
    }
    // Build the diff: only include kinds whose staged value differs from the
    // current server-side state. Saves a no-op trip when the operator clears
    // a row that already had no override.
    const edits = this.overrideEdits();
    const body: QuotaOverrideRequest = { reason };
    for (const k of this.QUOTA_KINDS) {
      const editVal = edits[k];
      if (editVal === undefined) continue;
      const cell = detail.firestore.dailyQuota[k];
      if (editVal === 'unset') {
        if (cell.hasOverride) body[k] = 'unset';
        continue;
      }
      const initialVal: number | null | undefined = cell.hasOverride ? cell.override : undefined;
      if (editVal !== initialVal) body[k] = editVal;
    }
    await this.runAction(
      () => this.adminService.setUserQuotaOverride(detail.pg.uid, body),
      'Override сохранён',
      detail.pg.uid,
    );
    // applyDetail() reseeds overrideEdits from the fresh server state in runAction.
  }

  async resetQuota(detail: AdminUserDetail) {
    const reason = (this.overrideReason() || '').trim()
      || prompt('Причина сброса квоты?')
      || '';
    if (!reason) return;
    if (!confirm(`Сбросить дневные счётчики для ${detail.pg.email || detail.pg.uid}? Reason: ${reason}`)) return;
    await this.runAction(
      () => this.adminService.resetUserQuota(detail.pg.uid, reason),
      'Дневная квота сброшена',
      detail.pg.uid,
    );
  }

  isSelf(detail: AdminUserDetail): boolean {
    return detail.pg.uid === this.firebase.currentUser()?.uid;
  }

  async togglePro(detail: AdminUserDetail) {
    const next = !detail.pg.is_pro;
    if (!confirm(`${next ? 'Выдать' : 'Снять'} PRO для ${detail.pg.email || detail.pg.display_name || detail.pg.uid}?`)) return;
    await this.runAction(() => this.adminService.setPro(detail.pg.uid, next), `${next ? 'PRO выдан' : 'PRO снят'}`, detail.pg.uid);
  }

  async setRole(detail: AdminUserDetail, role: 'user' | 'admin') {
    if (!confirm(`Изменить роль ${detail.pg.email || detail.pg.uid} → ${role}?`)) return;
    await this.runAction(() => this.adminService.setRole(detail.pg.uid, role), `Роль изменена → ${role}`, detail.pg.uid);
  }

  async refreshClaims(detail: AdminUserDetail) {
    await this.runAction(() => this.adminService.refreshClaims(detail.pg.uid), 'Claims обновлены — применятся на следующем getIdToken() (≤1ч)', detail.pg.uid);
  }

  async softDelete(detail: AdminUserDetail) {
    const label = detail.pg.email || detail.pg.display_name || detail.pg.uid;
    if (!confirm(`Soft-delete пользователя ${label}? Сессии будут отозваны.`)) return;
    if (!confirm(`Точно? ${label} перестанет иметь доступ к сервису.`)) return;
    await this.runAction(() => this.adminService.softDeleteUser(detail.pg.uid), 'Пользователь soft-deleted', detail.pg.uid);
  }

  private async runAction(fn: () => Promise<any>, successMsg: string, refreshUid: string) {
    this.busy.set(true);
    this.actionError.set(null);
    this.actionMessage.set(null);
    try {
      await fn();
      this.actionMessage.set(successMsg);
      // Refresh user detail — applyDetail() reseeds overrideEdits from the
      // fresh server state, so cleared rows show as «no override» again.
      const fresh = await this.adminService.getUser(refreshUid);
      this.applyDetail(fresh);
      // Also refresh list
      this.search(this.offset());
    } catch (err: any) {
      if (err instanceof AdminForbiddenError) { this.forbidden.emit(); return; }
      this.actionError.set(err?.message || 'Ошибка действия');
    } finally {
      this.busy.set(false);
    }
  }

  async loadActivity(uid: string) {
    this.loadingActivity.set(true);
    try {
      const a = await this.adminService.getUserActivity(uid, 20);
      this.activity.set(a);
    } catch (err: any) {
      if (err instanceof AdminForbiddenError) { this.forbidden.emit(); return; }
      this.actionError.set(err?.message || 'Ошибка загрузки активности');
    } finally {
      this.loadingActivity.set(false);
    }
  }

  formatDate(iso: string): string {
    try { return new Date(iso).toLocaleString('ru-RU', { dateStyle: 'short', timeStyle: 'short' }); }
    catch { return iso; }
  }

  formatBytes(b: any): string {
    if (typeof b !== 'number' || !b) return '—';
    if (b < 1024) return `${b} B`;
    if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
    if (b < 1024 * 1024 * 1024) return `${(b / 1024 / 1024).toFixed(1)} MB`;
    return `${(b / 1024 / 1024 / 1024).toFixed(2)} GB`;
  }
}
